import { BACKEND } from '../consts/backend';

/**
 * Funciones para manejar la selección de empresa y base de datos
 */

// Obtener empresa seleccionada del localStorage
export const getSelectedCompany = () => {
  try {
    const company = localStorage.getItem('selectedCompany');
    return company ? JSON.parse(company) : null;
  } catch (error) {
    console.error('Error getting selected company:', error);
    return null;
  }
};

// Obtener base de datos actual del localStorage
export const getCurrentDatabase = () => {
  return localStorage.getItem('currentDatabase') || null;
};

// Verificar si hay una empresa seleccionada
export const hasSelectedCompany = () => {
  const company = getSelectedCompany();
  const database = getCurrentDatabase();
  return !!(company && database);
};

// Limpiar selección de empresa (logout)
export const clearCompanySelection = async () => {
  try {
    // Limpiar base de datos en el servidor
    const response = await fetch(`${BACKEND}/db/clear`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('No se pudo limpiar la DB en el servidor, continuando...');
    }
  } catch (error) {
    console.warn('Error limpiando DB en servidor:', error);
  } finally {
    // Siempre limpiar localStorage
    localStorage.removeItem('selectedCompany');
    localStorage.removeItem('currentDatabase');
  }
};

// Verificar estado de la base de datos en el servidor
export const checkDatabaseStatus = async () => {
  try {
    const response = await fetch(`${BACKEND}/db/status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: data.success,
        database: data.database,
        isDefault: data.is_default
      };
    }

    return { success: false, database: null, isDefault: true };
  } catch (error) {
    console.error('Error checking database status:', error);
    return { success: false, database: null, isDefault: true };
  }
};

// Configurar empresa en el servidor
export const setCompanyInServer = async (database) => {
  try {
    const response = await fetch(`${BACKEND}/db/set/${database}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error setting company in server:', error);
    return { success: false, message: 'Error de conexión' };
  }
};

// Función para configurar headers por defecto en todas las requests
export const getRequestHeaders = () => {
  const currentDatabase = getCurrentDatabase();
  const headers = {
    'Content-Type': 'application/json'
  };
  
  if (currentDatabase) {
    headers['X-Database'] = currentDatabase;
  }
  
  return headers;
};

// Función helper para hacer requests con headers automáticos
export const fetchWithDatabase = async (url, options = {}) => {
  const currentDatabase = getCurrentDatabase();
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(currentDatabase && { 'X-Database': currentDatabase })
  };

  const requestOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  };
  
  return fetch(url, requestOptions);
};

// Hook personalizado para validar empresa seleccionada
export const useCompanyValidation = () => {
  const validateCompany = () => {
    return hasSelectedCompany();
  };

  const requireCompany = (callback) => {
    if (!validateCompany()) {
      // Redirigir a selección de empresa
      window.location.href = `${process.env.PUBLIC_URL}/company-selection`;
      return false;
    }
    if (callback) callback();
    return true;
  };

  return {
    validateCompany,
    requireCompany,
    hasCompany: hasSelectedCompany(),
    company: getSelectedCompany(),
    database: getCurrentDatabase()
  };
};

// Información de empresas disponibles
export const COMPANIES = {
  dow: {
    id: 'dow',
    name: 'DOW',
    database: 'ssb_internacional',
    description: 'Dow Chemical Company',
    color: '#e74c3c',
    logo: '🏭'
  },
  bostik: {
    id: 'bostik',
    name: 'Bostik',
    database: 'ssb_internacional_bostik', // -> utilizar esta cuando este
    description: 'Bostik Adhesives',
    color: '#2980b9',
    logo: '🔧'
  }
};

export default {
  getSelectedCompany,
  getCurrentDatabase,
  hasSelectedCompany,
  clearCompanySelection,
  checkDatabaseStatus,
  setCompanyInServer,
  useCompanyValidation,
  getRequestHeaders,
  fetchWithDatabase,
  COMPANIES
};
